import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styled from "@emotion/styled";

import { paper, fonts } from "../theme";
import { useWebSocket } from "../useWebSocket";
import * as haptics from "../haptics";
import type { ResultsView } from "../protocol";

import {
    MsScreen, MsBody, MsFooter,
    MsDisplay, MsEyebrow, MsRuleRed,
    MsPaper, MsTape, MsBtn, MsWaitingFor,
    MsQuoteWrap, MsBigQuote,
    MsSourceNote, MsSourceLink,
    MaskHeadline, ProgressPips,
    Stack, Row,
} from "../ms";

const PromptEyebrow = styled(MsEyebrow)`
    font-style: italic;
    text-transform: none;
    letter-spacing: 0;
`;

const EntryPaper = styled(MsPaper)`
    padding: 30px 22px 26px;
    transform: rotate(-1deg);
`;

const EntryDisplay = styled(MsDisplay)`
    font-size: 30px;
    font-weight: 700;
    position: relative;
`;

const EntryRule = styled(MsRuleRed)`
    margin-top: 18px;
`;

const EntryMetaRow = styled(Row)`
    margin-top: 8px;
`;


const EntryAuthor = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 700;
    font-size: 18px;
    color: ${paper.inkSoft};
`;

const PreRevealPaper = styled(MsPaper)`
    padding: 30px 22px;
    text-align: center;
`;

const PreRevealDisplay = styled(MsDisplay)`
    font-size: 26px;
`;

interface Props {
    view: ResultsView;
}

export default function Results({ view }: Props) {
    const { sendMessage } = useWebSocket();
    const { entries, highlightedIndex, isPresenter, presenterName, prompt } = view;

    /* `entries` arrives in reveal order — the server shuffles it per round and the
       list's order is the wire contract, with highlightedIndex pointing into it.
       (It used to be keyed by playerId; re-sorting it by the roster here made every
       reveal come out in joining order and defeated the shuffle.) */
    const total = entries.length;
    const cur = highlightedIndex >= 0 ? entries[highlightedIndex] : null;
    const isLast = highlightedIndex >= total - 1;

    /* Same summons as the prompt picker gets: the room is now waiting on you to
     * read the entries out, and nobody is looking at their own screen. */
    useEffect(() => {
        if (isPresenter) haptics.yourTurn();
    }, [isPresenter]);

    const advance = () => sendMessage({ type: "advance_results" });

    return (
        <MsScreen>
            <MsBody>
                <Stack gap={16} align="stretch">
                    <Row align="center" justify="space-between">
                        <MsEyebrow>
                            {highlightedIndex >= 0 ? `Entry ${highlightedIndex + 1} of ${total}` : `${total} entries`}
                        </MsEyebrow>
                        <PromptEyebrow tone="mute">
                            <em>"{prompt.replace(/___/g, "_____")}"</em>
                        </PromptEyebrow>
                    </Row>

                    <ProgressPips count={total} index={Math.max(0, highlightedIndex)} />

                    <AnimatePresence mode="wait">
                        {cur ? (
                            <motion.div
                                key={`entry-${highlightedIndex}`}
                                layoutId={highlightedIndex === 0 ? "compose-strip" : undefined}
                                initial={{ opacity: 0, y: 14, scale: 0.96 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -14, scale: 0.98 }}
                                transition={{ type: "spring", stiffness: 220, damping: 24 }}
                            >
                                <EntryPaper deckle r0="-3deg" r1="-1deg">
                                    <MsTape placement="tl" />
                                    <MsQuoteWrap>
                                        <MsBigQuote>"</MsBigQuote>
                                        <EntryDisplay>
                                            <MaskHeadline delay={0.1}>
                                                {cur.answer || "(No answer)"}
                                            </MaskHeadline>
                                        </EntryDisplay>
                                    </MsQuoteWrap>
                                    <EntryRule />
                                    <EntryMetaRow justify="space-between" align="center">
                                        {/* The one place the source is reachable.
                                            The round is over, so there's no clock
                                            to lose by tapping out — unlike the word
                                            picker, whose timeout submits for you. */}
                                        <MsSourceNote>
                                            {cur.article ? (
                                                <>from <MsSourceLink
                                                    href={cur.article.url}
                                                    label={cur.article.title}
                                                /></>
                                            ) : "from —"}
                                        </MsSourceNote>
                                        <EntryAuthor>
                                            {cur.playerName}
                                        </EntryAuthor>
                                    </EntryMetaRow>
                                </EntryPaper>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="pre-reveal"
                                layoutId="compose-strip"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                <PreRevealPaper deckle r1="-1deg">
                                    <MsTape placement="tl" />
                                    <PreRevealDisplay>
                                        <MaskHeadline>{total} {total === 1 ? "entry" : "entries"} ready.</MaskHeadline>
                                    </PreRevealDisplay>
                                </PreRevealPaper>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Stack>
            </MsBody>
            <MsFooter>
                {isPresenter ? (
                    <MsBtn variant="red" onClick={advance}>
                        {highlightedIndex < total - 1 ? "Next entry" : isLast ? "Start voting" : "Reveal next"}
                    </MsBtn>
                ) : (
                    <MsWaitingFor name={presenterName} />
                )}
            </MsFooter>
        </MsScreen>
    );
}
